import { useState } from "react";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import Screen from "../components/Screen";
import TypeText from "../components/TypeText";
import LandingValues from "../components/LandingValues";
import { PrimaryBtn, GhostBtn } from "../components/Buttons";
import Notice from "../components/Notice";
import Ico, { infoIcon } from "../components/Ico";
import { useSupplier } from "../SupplierContext";
import { CylinderCustomer, CylinderInfo, isExistingPhone, findCustomerByPhone, maskPhone, toMatchedCustomer } from "../demoData";
import { matchByPhone } from "../registerApi";
import { maskHouseNo, normalizeHouseNo, isValidHouseNo } from "@utils/func/houseNo";

interface ExistingPhoneProps {
  /** 由掃碼比對到的既有客戶（遮罩資料）；有值時直接顯示「這是您嗎」確認卡。 */
  matched?: CylinderCustomer | null;
  /** 掃 barcode 的狀態（已註冊/失效/查無）→ 顯示對應提示。 */
  reason?: CylinderInfo["state"];
  /** matched=true 時代表使用者在確認卡上按了「這是我」（不論命中來源是 props.matched 或本頁內部比對），上層應直接呼叫 walk-in 綁定。
   *  houseNo＝客戶在確認卡輸入的門牌號（已正規化），供上層送後端 verifyClaimedCis 驗證；未命中既有客戶時為 undefined。 */
  onDone: (phone: string, matched?: boolean, houseNo?: string) => void;
  /** 「不是我」→ 改走手機比對（帶出已填的手機號碼） */
  onReject?: (phone: string) => void;
  /** 需先驗 OTP（掃 QR·不能掃描）：不在本頁做即時比對，一律交回上層先驗證再確認客戶資料 */
  requireOtp?: boolean;
  /** 確認卡只顯示客戶資料、不顯示手機輸入框（號碼已於前面 OTP 取得，如傳單·有指定 / QR·不能掃描） */
  hidePhoneField?: boolean;
  /** 確認卡「這是我」按鈕文字（依流程終點：QR→前往下單、傳單→前往主頁） */
  confirmLabel?: string;
  /** 上層正在呼叫 walk-in 綁定既有客戶（免填直接綁）→ 停用按鈕、顯示送出中。 */
  confirmBusy?: boolean;
  /** 上層呼叫 walk-in 綁定失敗的錯誤訊息（已同時 toast 一次，這裡另外常駐顯示，避免卡在半完成畫面）。 */
  errorText?: string;
  /** 省略＝這一頁沒有上一步（例：瓦斯行關閉掃描，掃碼直接落在本頁）→ 不顯示返回鍵。 */
  onBack?: () => void;
}

/**
 * 手機閘門 + 「這是您嗎」確認：
 * - 掃碼已比對到 / 輸入的門號在既有名單 → 顯示確認卡 →「這是我」→ 一鍵下單（OTP 留結帳）。
 * - 比對不到 → 交回上層（問是否叫過瓦斯）。
 */
const ExistingPhone = ({ matched, reason, onDone, onReject, requireOtp, hidePhoneField, confirmLabel = "這是我，前往下單", confirmBusy, errorText, onBack }: ExistingPhoneProps) => {
  const supplier = useSupplier();
  const { lineAccessToken } = useLineAuth();
  const { entrySupplierId, phoneVerifiedTicket, setMatchedCisId } = useKiosk();
  const [ph, setPh] = useState("09");
  const [phoneCust, setPhoneCust] = useState<CylinderCustomer | null>(null); // 輸入手機比對到的客戶
  const [matching, setMatching] = useState(false);
  const [houseNo, setHouseNo] = useState(""); // 確認卡上客戶輸入的門牌號（身分驗證用）
  const digits = ph.replace(/\D/g, "");
  const valid = /^09\d{8}$/.test(digits);
  const shownCust = matched ?? phoneCust; // 要顯示的「這是您嗎」客戶
  // 門牌號驗證：地址遮掉門牌號、改由客戶輸入比對。遮罩與輸入框是同一件事的兩半，不可只留其一
  // （只遮不問只會讓卡片更難辨識）。
  const houseNoOk = isValidHouseNo(houseNo);
  // 手機 input 是否顯示：自己打字比對到（phoneCust）或 OTP 已帶入（hidePhoneField）時不顯示；
  // 其餘（含掃碼比對到但未 OTP）都要客戶輸入手機。有顯示時按鈕須等號碼填妥（valid）才可按。
  const showPhoneInput = !phoneCust && !hidePhoneField;

  const reasonNote =
    reason === "joined" ? "此鋼瓶已被註冊，請輸入手機號碼確認您的身分。"
    : reason === "invalid" ? "此鋼瓶的 barcode 已失效，請輸入手機號碼繼續。"
    : reason === "new" ? "查無此鋼瓶的關聯，請輸入手機號碼繼續。"
    : null;

  // 初始落地狀態：還沒比對到人、也沒有 barcode 異常要交代 → 走招呼式標題並帶價值點。
  // 另兩種狀態（確認身分／barcode 異常）都是「有事要處理」，維持原本的功能性文案，不打招呼。
  const isLanding = !shownCust && !reasonNote;

  const submit = async () => {
    // 確認卡上「這是我」→ 上層呼叫 walk-in 免填直接綁；帶上客戶輸入的門牌號供後端驗證宣稱的 CIS
    if (shownCust) { onDone(digits, true, normalizeHouseNo(houseNo)); return; }
    // 需先驗 OTP（不能掃描）：不在本頁即時比對，交回上層先驗證再顯示客戶資料
    if (requireOtp) { onDone(digits); return; }

    // 沒有真實 supplier_id（shopCode 尚未解析出/入口非真實瓦斯行）→ 無法比對真後端，維持 demo fallback
    if (!entrySupplierId) {
      if (isExistingPhone(digits)) { setPhoneCust(findCustomerByPhone(digits)); return; }
      onDone(digits);
      return;
    }

    setMatching(true);
    try {
      const res = await matchByPhone({ accessToken: lineAccessToken || "", supplier_id: entrySupplierId, phone: digits, phone_verified_ticket: phoneVerifiedTicket || undefined });
      const data = res.data?.data;
      // 地址不完整（沒地址／沒門牌號／拆不出路名）＝這筆不給認領 → 與「查無」完全同路，
      // 交上層走沒命中分流（2026-08-12）。留在確認卡只會走進 HOUSE_NO_UNVERIFIABLE 死路。
      if (data?.matched && !data.customer?.address_incomplete) {
        const cisId = data.customer?.cis_id;
        if (cisId) setMatchedCisId(cisId); // 供後續 walk-in 綁定（後端仍會重驗）
        setPhoneCust(
          toMatchedCustomer({
            phone: maskPhone(digits),
            address_masked: data.customer?.address_masked,
            addresses: data.customer?.addresses,
          }),
        );
      } else {
        onDone(digits); // 查無／命中但地址不完整 → 交上層（問家用電話 / 是否叫過桶）
      }
    } catch {
      // 比對失敗（網路/後端錯誤）→ 不擋流程，交上層繼續走
      onDone(digits);
    } finally {
      setMatching(false);
    }
  };
  const reject = () => {
    setPhoneCust(null); // 清掉手機比對的確認卡
    setHouseNo(""); // 門牌號只屬於這一張確認卡，不可帶著殘留值進下一輪比對
    onReject?.(digits); // 「不是我」一律交上層決定去向（QR → 家用電話；傳單 → 退回手機閘門重填）
  };

  return (
    <Screen
      title={isLanding ? <span className="inline-block text-[34px] leading-[1.15]"><TypeText text="Hi，歡迎使用" /></span> : shownCust ? "確認身分" : "輸入手機號碼"}
      subtitle={
        // 瓦斯行名稱來自 shopCode 解析；查不到時只有 supplier_id、沒有名字，
        // 直接串會變成開頭空一格的「 已經上線」→ 沒有名字就用不具名說法。
        isLanding ? <>{supplier.name ? <>{supplier.name} 已經上線<br /></> : null}輸入手機號碼即可開始使用。</>
        : shownCust ? "輸入門牌號碼確認是您本人，即可帶入您的既有資料"
        : "以手機號碼確認您的身分"
      }
      onBack={onBack}
      footer={
        <>
          <PrimaryBtn
            active={(showPhoneInput ? valid : !!shownCust) && (!shownCust || houseNoOk) && !matching && !confirmBusy}
            onClick={submit}
          >
            {confirmBusy ? "送出中…" : matching ? "比對中…" : shownCust ? confirmLabel : "繼續"}
          </PrimaryBtn>
          {shownCust && !matching && !confirmBusy && <GhostBtn onClick={reject}>不是我</GhostBtn>}
        </>
      }
    >
      {shownCust && (
        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-[14px] p-4 mb-5">
          <div className="flex items-center gap-2.5 mb-3.5">
            <div className="w-8 h-8 rounded-[9px] bg-[#10B981] flex items-center justify-center shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="text-[13px] font-extrabold text-[#15803D]">已比對到您的資料，這是您嗎？</div>
          </div>
          <div className="flex flex-col gap-3">
            {/* 瓦斯行名稱來自 shopCode 解析；查不到名字（只有 supplier_id）時整段不顯示，避免空白列。 */}
            {supplier.name && (
              <div>
                <div className="text-[11.5px] text-[#64748B] font-semibold mb-0.5">瓦斯行</div>
                <div className="flex items-center gap-1.5">
                  <Ico name="store" c="#1565C0" s={15} />
                  <span className="text-[15px] text-[#1565C0] font-bold leading-[1.5]">{supplier.name}</span>
                </div>
                {supplier.phone && (
                  <div className="text-[12.5px] text-[#475569] font-semibold mt-0.5">{supplier.phone}</div>
                )}
              </div>
            )}
            {shownCust.phone && (
              // 加帳戶模式的免OTP確認卡（OA entry-check 命中）沒有手機可顯示（後端只回地址，
              // 電話用註冊時已驗證的 main_phone，不回傳遮罩值給前端）→ 整段不顯示，避免空白列。
              <div>
                <div className="text-[11.5px] text-[#64748B] font-semibold mb-0.5">電話</div>
                <div className="text-[15px] text-[#0f1a35] font-bold leading-[1.5]">{shownCust.phone}</div>
              </div>
            )}
            <div>
              <div className="text-[11.5px] text-[#64748B] font-semibold mb-1.5">
                地址{(shownCust.addresses?.length ?? 0) > 1 ? `（共 ${shownCust.addresses!.length} 筆）` : ""}
              </div>
              <div className="flex flex-col gap-2">
                {(shownCust.addresses && shownCust.addresses.length > 0
                  ? shownCust.addresses
                  : [{ address_masked: shownCust.addr, is_represent: true }]
                ).map((a, i) => {
                  const primary = a.is_represent;
                  return (
                    <div
                      key={`${a.address_masked}-${i}`}
                      className={
                        primary
                          ? "rounded-[12px] border-[1.5px] border-[#2196f3] bg-[#EFF6FF] px-3 py-2.5"
                          : "rounded-[12px] border border-[#E2E8F0] bg-white/70 px-3 py-2.5"
                      }
                    >
                      <div className="flex items-start gap-2">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="shrink-0 mt-[2px]"
                          aria-hidden
                        >
                          {primary ? (
                            <path
                              d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z"
                              fill="#2196f3"
                            />
                          ) : (
                            <path
                              d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z"
                              stroke="#94A3B8"
                              strokeWidth="1.6"
                              fill="none"
                            />
                          )}
                        </svg>
                        <div className="flex-1 min-w-0">
                          {primary && (
                            <span className="inline-block text-[10px] font-extrabold tracking-wide text-[#1565C0] bg-white border border-[#BFDBFE] rounded-full px-2 py-[2px] mb-1">
                              主要
                            </span>
                          )}
                          <div
                            className={
                              primary
                                ? "text-[14px] text-[#0f1a35] font-bold leading-[1.45]"
                                : "text-[13.5px] text-[#475569] font-semibold leading-[1.45]"
                            }
                          >
                            {maskHouseNo(a.address_masked)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 門牌號輸入放在確認卡「外面」：卡片是系統給的資料，這裡是要客戶回答的問題，兩者不混在一起。 */}
      {shownCust && (
        <div className="mb-5">
          <div className="text-[13px] text-[#475569] font-semibold mb-2">
            {(shownCust.addresses?.length ?? 0) > 1
              ? "請輸入「主要」地址的門牌號碼"
              : "請輸入上述地址的門牌號碼"}
          </div>
          <div className="flex items-center gap-2.5">
            <input
              value={houseNo}
              onChange={(e) => setHouseNo(e.target.value)}
              inputMode="numeric"
              placeholder="例：20"
              aria-label="門牌號碼"
              className="flex-1 h-[54px] px-4 border-none rounded-[14px] bg-[#F1F5F9] text-[17px] outline-none box-border text-[#333]"
            />
            <div className="text-[15px] font-bold text-[#0f1a35] shrink-0">號</div>
          </div>
          {!!houseNo && !isValidHouseNo(houseNo) && (
            <div className="mt-2 text-[12px] text-[#EF4444] font-semibold">請只填門牌號碼，例：20 或 20-1。</div>
          )}
        </div>
      )}

      {!!errorText && (
        <div className="mb-4 text-[13px] font-semibold text-[#EF4444] text-center">{errorText}</div>
      )}

      {!shownCust && reasonNote && (
        <div className="mb-4">
          <Notice tone="amber" icon={infoIcon}>{reasonNote}</Notice>
        </div>
      )}

      {/* 手機號碼輸入框（可編輯、不自動帶入）。
          只隱藏「自己打字比對到（phoneCust）」這條——號碼剛輸入過，確認卡下再放 input 會重複。
          掃碼比對到身分（matched、尚未 OTP）仍要留 input 讓客戶輸入手機確認；經 OTP 帶入的（hidePhoneField）則隱藏。 */}
      {showPhoneInput && (
        <>
          <div className="text-[13px] text-[#475569] font-semibold mb-2">電話</div>
          <div className="flex gap-2.5">
            <div className="flex items-center px-4 h-[54px] rounded-[14px] bg-[#F1F5F9] font-bold text-base text-[#333] shrink-0">+886</div>
            <input
              value={ph}
              onChange={(e) => setPh(e.target.value)}
              inputMode="numeric"
              placeholder="0912 345 678"
              className="flex-1 h-[54px] px-4 border-none rounded-[14px] bg-[#F1F5F9] text-[17px] outline-none box-border text-[#333]"
            />
          </div>
          {digits.length > 10 && (
            <div className="mt-2 text-[12px] text-[#EF4444] font-semibold">手機號碼為 10 碼，請確認是否多打了。</div>
          )}
        </>
      )}

      {isLanding && <LandingValues />}

      {!shownCust && !!reasonNote && (
        <div className="mt-[18px]">
          <Notice tone="blue" icon={infoIcon}>
            輸入後會為您確認身分；查不到會再請您確認是否曾在此叫過瓦斯。
          </Notice>
        </div>
      )}
    </Screen>
  );
};

export default ExistingPhone;
